import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClientDocument, ClientDocumentHelper, DocumentChildrenMap } from '../../core/models/client-document';
import { ClientDocumentService, IClientDocumentUploadResult } from '../../core/services/client-document.service';
import { MatTableDataSource } from '@angular/material/table';
import { IClientDocumentSelection } from '../document-select/document-select.component';
import { MatterService } from '../../core/services/matter.service';
import { OTHER_OPTION } from '../../core/shared/constants';
import { forEach } from 'lodash-es';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

interface FileData {
  Files: DocumentFile[];
}

interface DocumentFile {
  FileName: string;
  FileSize: number;
  TimeUploaded: string;
  Hash: string;
}

@Component({
  selector: 'app-document-details-dialog',
  templateUrl: './document-details-dialog.component.html',
  styleUrl: './document-details-dialog.component.scss'
})
export class DocumentDetailsDialogComponent implements OnInit {

  document: ClientDocument | null;
  documents: ClientDocument[] = [];
  documentSelection: ClientDocument[] = [];
  childDocuments: ClientDocument[] = [];
  children: DocumentChildrenMap = {};
  displayedColumns: string[] = ['description', 'hash', 'uploaded', 'size'];
  dataSource = new MatTableDataSource(this.documents);
  addItem: boolean = false;
  matterId: string | undefined;
  loading: boolean = false;
  uploadResultMessage: any;
  uploadResultMessageType: any;
  uploadErrorDetail: any;
  fadeOutAlert: boolean = false;
  isSmallScreen: boolean = false;
  attachmentUploaded: boolean = false;
  confirmedUploadExtraDocs: boolean = false;

  @Output() itemsUploaded = new EventEmitter<any>();
  isMessageAttachment: boolean = false;

  constructor(@Inject(MAT_DIALOG_DATA) public data: { document: ClientDocument, documents: ClientDocument[], documentSelection: ClientDocument[], matterId?: string, isMessageAttachment?: boolean }, private clientDocumentService: ClientDocumentService, private matterService: MatterService, private snackBar: MatSnackBar, private breakpointObserver: BreakpointObserver) {
    this.document = data.document;
    // if this was opened by "add item" we want the list of all documents here 
    this.documents = data.documents;
    this.documentSelection = data.documentSelection ?? [];
    this.matterId = data.matterId;
    this.isMessageAttachment = data.isMessageAttachment ?? false;
    /* there is no document, this must be from the "Add Item" button or a message attachment.
    We need to show the document select form */
    if (this.document == null) {
      this.addItem = true;
    }
    else {
      this.prepareDocumentFiles();
    }

  }

ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall]).subscribe((result: { matches: boolean; }) => {
      this.isSmallScreen = result.matches;
    });
  }


  prepareDocumentFiles() {

    this.childDocuments = this.documents.filter(d => !d.zipped && (d.parentId === this.document?.id || (d.id == this.document?.id && d.timeUploaded != null)));
    let zippedDocs = this.documents.filter(d => d.zipped && (d.parentId === this.document?.id || (d.id == this.document?.id && d.timeUploaded != null)));

    forEach(zippedDocs, (doc) => {
      if (doc.fileData != null && doc.fileData.length > 0) {
        const fileData = JSON.parse(doc.fileData) as FileData;
        let zippedDocs = fileData.Files.map(fd => {
          return {
            fileName: fd.FileName,
            fileSize: fd.FileSize,
            timeUploaded: fd.TimeUploaded,
            hash: fd.Hash
          } as ClientDocument;
        });
        this.childDocuments = this.childDocuments.concat(zippedDocs);
      }
    });

    this.childDocuments = this.childDocuments.filter(d => !d.zipped);

    this.childDocuments.sort((a, b) => {
      const dateA = new Date(a.timeUploaded || 0);
      const dateB = new Date(b.timeUploaded || 0);
      return dateB.getTime() - dateA.getTime();
    });

    this.dataSource = new MatTableDataSource(this.childDocuments);
  }

  refreshDocuments() {
    this.loading = true;
    const observable = this.matterId == null
      ? this.clientDocumentService.getAll()
      : this.matterService.getClientDocuments(this.matterId);

    observable.subscribe({
      next: (documents: ClientDocument[]) => {
        this.documents = documents;
        if (this.document) {
          this.prepareDocumentFiles();
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Failed to refresh documents.', 'Close', { duration: 4000, panelClass: 'snackbar-error' });
      }
    });
  }

  shouldShowDocumentDetails(): boolean {
    const status = this.getStatus();
    if(status === 'complete' || status === 'incomplete' || status === 'rejected') {
      return true;
    }
    else{
      return this.document != null && !this.isMessageAttachment && this.childDocuments.length > 0;
    }
    
  }

  handleDocumentSelection($event: { selection: IClientDocumentSelection; }) {

    this.childDocuments = [];
    if ($event.selection.document != null && $event.selection.document.id == OTHER_OPTION) {
      this.document = $event.selection.document;
    }
    else {
      this.document = this.documentSelection.find(d => d.id == $event.selection.document?.id) ?? null;
      if (this.document != null) {
        this.refreshDocuments();
      }
    }

  }

  // event emitted if a document upload fails
  handleOnError($event: any) {
    this.uploadResultMessage = "Error: Upload failed due to:  " + $event.error.detail;
    this.uploadResultMessageType = 'error';
  }

  // event emitted if a document upload is cancelled
  handleOnCancelled() {
    this.uploadResultMessage = "Upload was cancelled by user.";
    this.uploadResultMessageType = 'error';
  }

  //event emitted if a document is sucessfully uploaded
  handleOnUpload($event: any) {
    //set this to true so we don't get asked to confirm here in the case of this being a new document
    this.confirmedUploadExtraDocs = true;
    this.showSuccessAlert();
    // we just created a new document
    if (this.document && this.document.id == OTHER_OPTION) {
      this.document = $event.result.document;
      this.addItem = false;
    }
    this.refreshDocuments();

    if(this.isMessageAttachment){
      this.attachmentUploaded = true;
      this.itemsUploaded.emit({ document: $event.result.document, files: $event.result.files } as IClientDocumentUploadResult);
    }

  }

  showSuccessAlert() {
    this.uploadResultMessageType = 'success';
    this.uploadResultMessage = 'Upload successful!';
    setTimeout(() => {
      this.fadeOutAlert = true;
      setTimeout(() => {
        this.uploadResultMessage = null;
        this.fadeOutAlert = false;
      }, 500);
    }, 2500);
  }

  getStatus() {
    if (this.document != null) {
      return ClientDocumentHelper.getStatus(this.document);
    }
    return 'other';
  }

getTruncatedFileName(fileName: string, maxBaseLength: number = 40): string {

if(this.isSmallScreen){
  maxBaseLength = 20;
}

  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1) return fileName;
  const base = fileName.substring(0, lastDot);
  const ext = fileName.substring(lastDot);
  if (base.length > maxBaseLength) {
    return base.substring(0, maxBaseLength) + '[...]' + ext;
  }
  return fileName;
}

}
